/**
 * WizardTopBar
 *
 * Fixed top bar for the onboarding wizard.
 * Shows: contextual title text (left) + Skip Onboarding button (right) + Reset (Dev) button (right).
 * Skip Onboarding is hidden on the Completion screen (step 7).
 * Reset (Dev) button is only shown in non-production environments.
 */

import { RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/providers/AuthProvider';
import { useOnboarding, type OnboardingStep } from '@/contexts/OnboardingContext';

interface WizardTopBarProps {
  currentStep: OnboardingStep;
  title: string;
}

export function WizardTopBar({ currentStep, title }: WizardTopBarProps) {
  const { isProduction } = useAuth();
  const { dismissOnboarding, resetOnboarding } = useOnboarding();

  const isCompletionScreen = currentStep === 8;

  return (
    <div className="flex items-center justify-between h-14 px-4 border-b border-gray-100 bg-white shrink-0">
      {/* Left: title */}
      <div>
        <h2 className="text-base font-semibold text-gray-900">{title}</h2>
      </div>

      {/* Right: action buttons */}
      <div className="flex items-center gap-2">
        {/* Reset Dev button — non-production only */}
        {isProduction !== true && (
          <Button
            variant="outline"
            size="sm"
            className="text-xs text-orange-600 border-orange-300 hover:bg-orange-50 hover:text-orange-700 gap-1"
            onClick={resetOnboarding}
            title="Reset onboarding (dev only)"
          >
            <RotateCcw className="w-3 h-3" />
            Reset (Dev)
          </Button>
        )}

        {/* Skip Onboarding — hidden on completion screen */}
        {!isCompletionScreen && (
          <Button
            variant="ghost"
            size="sm"
            className="text-sm text-gray-500 hover:text-gray-700"
            onClick={dismissOnboarding}
          >
            Skip Onboarding
          </Button>
        )}
      </div>
    </div>
  );
}
